import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Search, MoreHorizontal, Power, Users, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  BackendUserRole,
  BackendUserStatus,
  PlatformTenant,
  PlatformUser,
  RoleMetadata,
  platformTenantsApi,
  platformUsersApi,
  rolesApi,
  userStatusToLabel,
} from "@/lib/platform-api";
import { ApiError } from "@/lib/api";
import { TenantSubNav } from "@/components/platform/TenantSubNav";
import { BulkUserImportDialog } from "@/components/BulkUserImportDialog";
import { InviteLinkDialog } from "@/components/InviteLinkDialog";
import { TENANT_ROLES, assignableRoles } from "@/lib/roles";

const STATUS_OPTIONS: { value: BackendUserStatus; label: string }[] = [
  { value: "ACTIVE", label: "Active" },
  { value: "DISABLED", label: "Disabled" },
  { value: "PENDING", label: "Pending" },
];

export default function TenantUsers() {
  const [search, setSearch] = useState("");
  const [tenantFilter, setTenantFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [roles, setRoles] = useState<RoleMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  /** Surfaced invite URL — see InviteLinkDialog comments. */
  const [inviteLink, setInviteLink] = useState<{
    name: string;
    email: string;
    url: string;
    variant: "new" | "reset";
  } | null>(null);

  useEffect(() => {
    platformTenantsApi.list({ pageSize: 200 }).then((r) => setTenants(r.data)).catch(() => { /* non-fatal */ });
    rolesApi.list().then(setRoles).catch(() => { /* non-fatal */ });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    platformUsersApi
      .list({
        pageSize: 200,
        search: search || undefined,
        tenantId: tenantFilter === "all" ? undefined : tenantFilter,
        status: statusFilter === "all" ? undefined : (statusFilter as BackendUserStatus),
      })
      .then((r) => { if (!cancelled) setUsers(r.data); })
      .catch((e) => {
        if (!cancelled) toast.error("Failed to load users", { description: errMsg(e) });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [search, tenantFilter, statusFilter, reloadKey]);

  const tenantUsers = users.filter(u => u.tenantId !== null);
  const active = tenantUsers.filter(u => u.status === "ACTIVE").length;
  const pending = tenantUsers.filter(u => u.status === "PENDING").length;
  const disabled = tenantUsers.filter(u => u.status === "DISABLED").length;

  const handleToggle = async (u: PlatformUser) => {
    try {
      if (u.status === "DISABLED") await platformUsersApi.enable(u.id);
      else await platformUsersApi.disable(u.id);
      toast.success(`${u.name} ${u.status === "DISABLED" ? "enabled" : "disabled"}`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Action failed", { description: errMsg(e) });
    }
  };

  return (
    <div className="space-y-5">
      <TenantSubNav />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total tenant users</div><div className="mt-1 font-display text-xl font-bold">{tenantUsers.length}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Active</div><div className="mt-1 font-display text-xl font-bold text-success">{active}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pending</div><div className="mt-1 font-display text-xl font-bold text-warning">{pending}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Disabled</div><div className="mt-1 font-display text-xl font-bold text-destructive">{disabled}</div></Card>
      </div>

      <Card>
        <CardHeader className="border-b pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2"><Users className="h-5 w-5 text-primary" /><CardTitle className="text-base">Cross-tenant users ({tenantUsers.length})</CardTitle></div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users..." className="h-9 w-56 pl-8" />
              </div>
              <Select value={tenantFilter} onValueChange={setTenantFilter}>
                <SelectTrigger className="h-9 w-44"><SelectValue placeholder="Tenant" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tenants</SelectItem>
                  {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 w-32"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All status</SelectItem>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <BulkImportPicker tenants={tenants} onDone={() => setReloadKey((k) => k + 1)} />
              <AddUserDialog
                tenants={tenants}
                roles={roles}
                onCreated={() => setReloadKey((k) => k + 1)}
                onInviteGenerated={(name, email, url) =>
                  setInviteLink({ name, email, url, variant: "new" })
                }
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
                <TableHead>Tenant</TableHead><TableHead>Status</TableHead><TableHead>Last login</TableHead>
                <TableHead>Created</TableHead><TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading users...</TableCell></TableRow>}
              {!loading && tenantUsers.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">No users match your filters.</TableCell></TableRow>}
              {!loading && tenantUsers.map(u => (
                <TableRow key={u.id}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent text-[10px] font-semibold text-white">{u.name.split(" ").map(n => n[0]).slice(0, 2).join("")}</div>
                      <span className="text-sm font-medium">{u.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell className="text-sm">{u.roleLabel ?? u.roleType}</TableCell>
                  <TableCell className="text-sm">{u.tenantName ?? "—"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      u.status === "ACTIVE" ? "border-success/30 bg-success/10 text-success" :
                      u.status === "PENDING" ? "border-warning/30 bg-warning/10 text-warning" :
                      "border-destructive/30 bg-destructive/10 text-destructive"
                    }>{userStatusToLabel(u.status)}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{new Date(u.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => handleToggle(u)}>
                          <Power className="mr-2 h-3.5 w-3.5" /> {u.status === "DISABLED" ? "Enable" : "Disable"} user
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>


      {inviteLink && (
        <InviteLinkDialog
          open={!!inviteLink}
          onClose={() => setInviteLink(null)}
          recipientName={inviteLink.name}
          recipientEmail={inviteLink.email}
          inviteUrl={inviteLink.url}
          variant={inviteLink.variant}
        />
      )}
    </div>
  );
}

function AddUserDialog({
  tenants,
  roles,
  onCreated,
  onInviteGenerated,
}: {
  tenants: PlatformTenant[];
  roles: RoleMetadata[];
  onCreated: () => void;
  onInviteGenerated: (name: string, email: string, url: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // No department field here: tenant departments are a managed, tenant-scoped
  // list (ADR-0008) — the tenant's own HR assigns them from the Users page.
  const [form, setForm] = useState({
    tenantId: "", name: "", email: "", phone: "",
    roleType: "HR_MANAGER" as BackendUserRole,
  });
  const tenantRoles = roles.filter(r => r.scope === "tenant");

  const submit = async () => {
    if (!form.tenantId) { toast.error("Select a tenant"); return; }
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email required"); return; }
    setSubmitting(true);
    try {
      const res = await platformUsersApi.create({
        tenantId: form.tenantId,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        roleType: form.roleType,
      });
      setOpen(false);
      setForm({ tenantId: "", name: "", email: "", phone: "", roleType: "HR_MANAGER" });
      onCreated();
      if (res.linkedToExisting) {
        toast.success(`${res.name} added`, {
          description: `Linked to their existing account (${res.email}). They sign in with their current password.`,
          duration: 8000,
        });
      } else if (res.inviteUrl) {
        onInviteGenerated(res.name, res.email, res.inviteUrl);
      } else {
        toast.success(`${res.name} created`, {
          description: `Invitation email sent to ${res.email}.`,
          duration: 6000,
        });
      }
    } catch (e) {
      toast.error("Could not create user", { description: errMsg(e) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-gradient-to-r from-accent to-primary hover:opacity-90">
          <Plus className="mr-1.5 h-4 w-4" /> Add user
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add user</DialogTitle>
          <DialogDescription>
            Create a new admin user under any tenant. They'll receive an email invitation to set
            up their account.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="grid gap-1.5">
            <Label>Tenant</Label>
            <Select value={form.tenantId} onValueChange={(v) => setForm({ ...form, tenantId: v })}>
              <SelectTrigger><SelectValue placeholder="Select a tenant" /></SelectTrigger>
              <SelectContent>
                {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" /></div>
          <div className="grid gap-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="user@tenant.com" /></div>
          <div className="grid gap-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+60..." /></div>
          <div className="grid gap-1.5">
            <Label>Role</Label>
            <Select value={form.roleType} onValueChange={(v) => setForm({ ...form, roleType: v as BackendUserRole })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {assignableRoles(tenantRoles.length > 0 ? tenantRoles : TENANT_ROLES).map(r => (
                  <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkImportPicker({ tenants, onDone }: { tenants: PlatformTenant[]; onDone: () => void }) {
  const [tenantId, setTenantId] = useState("");
  if (!tenantId) {
    return (
      <Select value={tenantId} onValueChange={setTenantId}>
        <SelectTrigger className="h-9 w-44"><SelectValue placeholder="Bulk import..." /></SelectTrigger>
        <SelectContent>
          {tenants.map((t) => <SelectItem key={t.id} value={t.id}>Import into {t.name}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  const tenant = tenants.find((t) => t.id === tenantId);
  return (
    <BulkUserImportDialog
      title={`Bulk import to ${tenant?.name ?? "tenant"}`}
      description="Paste CSV rows — each user receives an email invitation to set up their account."
      onImport={(rows) => platformUsersApi.bulkImport(tenantId, rows)}
      onCompleted={() => { setTenantId(""); onDone(); }}
    />
  );
}

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Unexpected error";
}
